<script setup>
// NOTICE: KAPTION-INTEGRATION
// Compact kanban card for a conversation in the sales funnel

import { computed, ref, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  stageColor: {
    type: String,
    default: '#0ea5e9',
  },
  isDarkMode: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

const { t } = useI18n();

// NOTICE: KAPTION-INTEGRATION - Privacy mode (blur card content, hover to reveal)
const kaptionPrivacyEnabled = inject('kaptionPrivacyEnabled', ref(false));
const privacyHovering = ref(false);
const isBlurred = computed(
  () => kaptionPrivacyEnabled.value && !privacyHovering.value
);

const contact = computed(() => props.conversation.meta?.sender || {});

const contactName = computed(
  () => contact.value.name || t('CONVERSATION.NO_CONTACT_NAME')
);

const contactThumbnail = computed(() => contact.value.thumbnail || '');

const lastMessage = computed(() => {
  const msg = props.conversation.messages?.[0];
  if (!msg) return props.conversation.last_non_activity_message?.content || '';
  return msg.content || '';
});

const lastMessagePreview = computed(() => {
  const text = lastMessage.value;
  if (!text) return '';
  // Strip HTML and truncate
  const stripped = text.replace(/<[^>]*>/g, '');
  return stripped.length > 80 ? `${stripped.slice(0, 80)}...` : stripped;
});

const timeAgo = computed(() => {
  const timestamp =
    props.conversation.last_activity_at || props.conversation.created_at;
  return timestamp ? dynamicTime(timestamp) : '';
});

const conversationId = computed(
  () => props.conversation.display_id || props.conversation.id
);

const assignee = computed(() => props.conversation.meta?.assignee || null);

const isUnread = computed(() => props.conversation.unread_count > 0);

const priorityColor = computed(() => {
  const p = props.conversation.priority;
  if (p === 'urgent') return '#ef4444';
  if (p === 'high') return '#f97316';
  if (p === 'medium') return '#fbbf24';
  return null;
});

const phoneNumber = computed(() => contact.value.phone_number || '');
</script>

<template>
  <div
    class="kanban-card group"
    :class="[
      isDarkMode ? 'kanban-card-dark' : 'kanban-card-light',
      { 'kaption-privacy-blur': isBlurred },
    ]"
    :style="{ borderLeftColor: stageColor }"
    @click="emit('click', conversation)"
    @mouseenter="privacyHovering = true"
    @mouseleave="privacyHovering = false"
  >
    <div class="flex items-start gap-2">
      <!-- Avatar -->
      <Avatar
        :src="contactThumbnail"
        :name="contactName"
        :size="24"
        rounded-full
      />
      <!-- Content -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center justify-between gap-1">
          <span
            class="text-sm font-medium truncate"
            :class="
              isDarkMode
                ? 'text-kpt-text-primary-dark'
                : 'text-kpt-text-primary-light'
            "
          >
            {{ contactName }}
          </span>
          <span
            class="text-[10px] shrink-0"
            :class="
              isDarkMode
                ? 'text-kpt-text-muted-dark'
                : 'text-kpt-text-muted-light'
            "
          >
            {{ timeAgo }}
          </span>
        </div>

        <!-- Last message preview -->
        <p
          v-if="lastMessagePreview"
          class="mt-0.5 text-xs leading-4 truncate"
          :class="
            isDarkMode
              ? 'text-kpt-text-secondary-dark'
              : 'text-kpt-text-secondary-light'
          "
        >
          {{ lastMessagePreview }}
        </p>

        <!-- Bottom row: ID badge + phone + assignee + priority -->
        <div class="flex items-center justify-between mt-1.5 gap-1">
          <div class="flex items-center gap-1.5">
            <span
              class="text-[10px] px-1 py-0.5 rounded font-mono"
              :class="
                isDarkMode
                  ? 'bg-kpt-dark-600 text-kpt-text-muted-dark'
                  : 'bg-kpt-light-300 text-kpt-text-muted-light'
              "
            >
              #{{ conversationId }}
            </span>
            <span
              v-if="phoneNumber"
              class="text-[10px] truncate max-w-[80px]"
              :class="
                isDarkMode
                  ? 'text-kpt-text-muted-dark'
                  : 'text-kpt-text-muted-light'
              "
            >
              {{ phoneNumber }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <!-- Unread badge -->
            <span
              v-if="isUnread"
              class="h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-medium rounded-full bg-kpt-emerald text-white"
            >
              {{
                conversation.unread_count > 9 ? '9+' : conversation.unread_count
              }}
            </span>
            <!-- Priority dot -->
            <span
              v-if="priorityColor"
              class="size-2 rounded-full shrink-0"
              :style="{ backgroundColor: priorityColor }"
            />
            <!-- Assignee avatar -->
            <Avatar
              v-if="assignee"
              :src="assignee.thumbnail || ''"
              :name="assignee.name || ''"
              :size="18"
              rounded-full
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.kanban-card {
  padding: 0.5rem 0.625rem;
  margin: 0 0.375rem 0.375rem;
  border-radius: 0.5rem;
  border-left: 3px solid;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  // Drag ghost styling
  &.sortable-ghost {
    opacity: 0.5;
    border-style: dashed;
    border-color: #6366f1;
  }

  &.sortable-drag {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    transform: scale(1.03) rotate(1deg);
  }
}

.kanban-card-dark {
  background: rgba(23, 23, 32, 0.9);
  border-right: none;
  border-top: none;
  border-bottom: none;

  &:hover {
    background: rgba(31, 31, 46, 0.95);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  }
}

.kanban-card-light {
  background: #ffffff;
  border-right: none;
  border-top: none;
  border-bottom: none;

  &:hover {
    background: #fafafa;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }
}

/* NOTICE: KAPTION-INTEGRATION - Privacy mode blur (no transition — animating blur is expensive) */
.kaption-privacy-blur {
  filter: blur(6px);
  user-select: none;
}
</style>
